'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { cn, isEdge } from '@core/utils';
import { useState, useTransition } from 'react';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { buildGiftOrderSearchParams, hasAllRequiredOptions } from '@/app/donation/_utils/giftOrder';
import { GIFT_SALE_STATUS, GIFT_SALE_STATUS_LABEL } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { DONATION_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
import { calculateGiftDonationAmount } from '../_utils/calculateGiftDonationAmount';
export interface SelectedGiftOption {
  optionGroupId: number;
  optionGroupName: string;
  optionItemId: number;
  optionItemName: string;
  extraProductPrice: number;
}
interface GiftDonateButtonProps {
  productInfo: GiftProductDetail;
  productNumber: string;
  selectedOptions?: SelectedGiftOption[];
  hasOptions?: boolean;
  customButtonText?: string;
  className?: string;
  dimensions?: 'H56' | 'H48' | 'H40';
  theme?: 'primary-fill' | 'sub-line' | 'sub-fill';
  requiredOptionGroupIds?: number[];
  onOptionNotSelected?: () => void;
  /** 주문 수량 */
  quantity?: number;
}
const SELECT_OPTION_MESSAGE = '답례품 옵션을 선택해주세요';
type ModalType = 'NONE' | 'EDGE';
export const GiftDonateButton = t0 => {
  const $ = _c(29);
  const {
    productInfo,
    productNumber,
    selectedOptions: t1,
    hasOptions: t2,
    customButtonText,
    className,
    dimensions: t3,
    theme: t4,
    requiredOptionGroupIds: t5,
    onOptionNotSelected,
    quantity: t6
  } = t0;
  const selectedOptions = t1 === undefined ? [] : t1;
  const hasOptions = t2 === undefined ? false : t2;
  const dimensions = t3 === undefined ? "H56" : t3;
  const theme = t4 === undefined ? "primary-fill" : t4;
  let t7;
  if ($[0] !== t5) {
    t7 = t5 === undefined ? [] : t5;
    $[0] = t5;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const requiredOptionGroupIds = t7;
  const quantity = t6 === undefined ? 1 : t6;
  const router = useCustomRouter();
  const {
    toast
  } = useToast();
  const [modalType, setModalType] = useState("NONE");
  const [isNavigating, startTransition] = useTransition();
  let t8;
  if ($[2] !== setModalType) {
    t8 = () => setModalType("NONE");
    $[2] = setModalType;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  useOverlayHistory(modalType !== "NONE", t8);
  const isDisabled = productInfo.productSaleStatus !== GIFT_SALE_STATUS.ON_SALE;
  const donationAmount = calculateGiftDonationAmount({
    currentPrice: productInfo.currentPrice,
    extraProductPrices: selectedOptions.map(_temp),
    quantity,
    hasOptions
  });
  const getButtonText = () => {
    if (customButtonText) {
      return customButtonText;
    }
    if (isDisabled && productInfo.productSaleStatus) {
      return GIFT_SALE_STATUS_LABEL[productInfo.productSaleStatus as GiftSaleStatus];
    }
    return `${donationAmount.toLocaleString("ko-KR")}원 기부하기`;
  };
  const navigateToOrder = () => {
    startTransition(() => {
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: buildGiftOrderSearchParams({
          institutionId: productInfo.institutionId,
          institutionName: productInfo.institutionName,
          productNumber,
          optionItemIds: hasOptions ? selectedOptions.map(_temp2) : [],
          quantity
        })
      }));
    });
  };
  const handleDonate = () => {
    if (isEdge()) {
      setModalType("EDGE");
      return;
    }
    const isMissingRequiredOption = hasOptions && !hasAllRequiredOptions(selectedOptions.map(_temp3), requiredOptionGroupIds);
    if (isMissingRequiredOption) {
      toast({
        message: SELECT_OPTION_MESSAGE,
        type: "info",
        offset: {
          bottom: 80
        }
      });
      onOptionNotSelected?.();
      return;
    }
    navigateToOrder();
  };
  const T0 = Button;
  const t9 = isDisabled && "border-Gray-100 bg-Gray-100 text-Gray-400";
  let t10;
  if ($[4] !== className || $[5] !== t9) {
    t10 = cn(className, t9);
    $[4] = className;
    $[5] = t9;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const t11 = getButtonText();
  let t12;
  if ($[7] !== T0 || $[8] !== dimensions || $[9] !== handleDonate || $[10] !== isDisabled || $[11] !== isNavigating || $[12] !== t10 || $[13] !== t11 || $[14] !== theme) {
    t12 = <T0 className={t10} dimensions={dimensions} theme={theme} loading={isNavigating} onClick={handleDonate} disabled={isDisabled}>{t11}</T0>;
    $[7] = T0;
    $[8] = dimensions;
    $[9] = handleDonate;
    $[10] = isDisabled;
    $[11] = isNavigating;
    $[12] = t10;
    $[13] = t11;
    $[14] = theme;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = modalType === "EDGE";
  let t14;
  if ($[16] !== setModalType) {
    t14 = () => setModalType("NONE");
    $[16] = setModalType;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <h2 className="font-700 text-Gray-900 typo-body2">웰로의 고향사랑기부제는 엣지 브라우저에서 지원되지 않습니다.</h2>;
    t16 = <p className="font-400 text-Gray-700 typo-body3">원활한 이용을 위해 Chrome, Safari 등의 브라우저를 이용해 주세요.</p>;
    $[18] = t15;
    $[19] = t16;
  } else {
    t15 = $[18];
    t16 = $[19];
  }
  let t17;
  if ($[20] !== setModalType) {
    t17 = <div className="flex flex-col gap-16 text-center">{t15}{t16}<Button onClick={() => setModalType("NONE")}>확인</Button></div>;
    $[20] = setModalType;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== t13 || $[23] !== t14 || $[24] !== t17) {
    t18 = <Modal opened={t13} onClose={t14}>{t17}</Modal>;
    $[22] = t13;
    $[23] = t14;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t12 || $[27] !== t18) {
    t19 = <>{t12}{t18}</>;
    $[26] = t12;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  return t19;
};
function _temp(option) {
  return option.extraProductPrice;
}
function _temp2(option_0) {
  return option_0.optionItemId;
}
function _temp3(option_1) {
  return option_1.optionGroupId;
}